// Routes of the dashboard. The session comes from /api/app/me (meQuery), cached by TanStack Query.
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { authOptionsQuery, meQuery } from './api';
import { RootLayout } from './layout';
import { ProfilePage, SecurityPage } from './pages/account';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage } from './pages/auth';
import { BillingPage } from './pages/billing';
import { HomePage } from './pages/home';
import { InvitationPage } from './pages/invitation';
import type { ReviewSearch } from './pages/manual-parts';
import { ManualReviewPage } from './pages/manual-review';
import { MembersPage } from './pages/members';
import { NewOrganizationPage } from './pages/organization';
import { PagesTab } from './pages/page-view';
import { ScanPage } from './pages/scan-page';
import { SealTab } from './pages/seal';
import {
  AnalysisTab,
  FollowTab,
  NewSitePage,
  SettingsTab,
  SitePage,
  VerificationTab,
  WordPressTab,
} from './pages/sites';
import { StatementTab } from './pages/statement';
import { ErrorPage, LoadingPage, NotFoundPage } from './pages/status';
import { UsersPage } from './pages/users';
import { ResendVerificationPage, VerifyEmailPage } from './pages/verification';
import { ConnectWordPressPage, IntegrationsPage } from './pages/wordpress';
import { followNavigations } from './ui';
import { safeRedirect } from './util';

interface RouterContext {
  queryClient: QueryClient;
}

/** The signed-in user, or a redirect to sign in (and back here afterwards). */
async function requireUser(context: RouterContext, href: string) {
  const me = await context.queryClient.fetchQuery(meQuery);
  if (!me) throw redirect({ to: '/login', search: { redirect: href === '/' ? undefined : href } });
  return me;
}

/** The signed-in user with an active organisation; without one, create it first. */
async function requireOrganization(context: RouterContext, href: string) {
  const me = await requireUser(context, href);
  if (!me.organization) throw redirect({ to: '/new-organization' });
  return me;
}

/** Pages for visitors: someone already signed in goes to the dashboard. */
async function visitorsOnly({ context }: { context: RouterContext }) {
  // What the sign-in screens offer is known before they draw, so the buttons do not appear late.
  const [me] = await Promise.all([
    context.queryClient.fetchQuery(meQuery),
    context.queryClient.ensureQueryData(authOptionsQuery),
  ]);
  if (me) throw redirect({ to: '/' });
}

const withRedirect = (search: Record<string, unknown>): { redirect?: string } => ({
  redirect: safeRedirect(search.redirect),
});

/** The sign-in screens also receive `?error=` when they are the place Google sends the person back to. */
const withRedirectAndError = (search: Record<string, unknown>): { redirect?: string; error?: string } => ({
  redirect: safeRedirect(search.redirect),
  error: typeof search.error === 'string' && search.error ? search.error : undefined,
});

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: HomePage,
});

const newSiteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/new',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: NewSitePage,
});

/** A website's page; its tabs are the routes below it, so the tabs stay in sight in all of them. */
const siteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: SitePage,
});

const siteAnalysisRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): { preparar?: true; comprobar?: string } => ({
    preparar: search.preparar === true || search.preparar === 'true' ? true : undefined,
    // The elements whose fix is being checked: their ids, separated by commas.
    comprobar:
      typeof search.comprobar === 'string' && /^[0-9a-f-]{36}(,[0-9a-f-]{36}){0,199}$/i.test(search.comprobar)
        ? search.comprobar
        : undefined,
  }),
  component: AnalysisTab,
});

/** Which analysis the "Pages" tab shows (the latest finished one by default), which page and on which device. */
export interface PagesSearch {
  scan?: string;
  p?: string;
  disp?: 'movil';
  /** An element of the page to open: the id of one of its occurrences (an issue and the position of the example). */
  e?: string;
  /** A problem of the page whose panel is open: its key (the rule and its result). */
  k?: string;
}

const pageKey = (value: unknown) =>
  typeof value === 'string' && /^[0-9a-f]{12}$/.test(value) ? value : undefined;

const uuid = (value: unknown) =>
  typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value) ? value : undefined;

/** The guide of a manual check and the form to record it; the search says what the review starts from. */
const siteManualRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'revision-manual/$criterion',
  validateSearch: (search: Record<string, unknown>): ReviewSearch => ({
    p: pageKey(search.p),
    occurrence: uuid(search.occurrence),
    previous: uuid(search.previous),
    r: uuid(search.r),
    scan:
      typeof search.scan === 'string' && /^[0-9A-Za-z-]{1,64}$/.test(search.scan) ? search.scan : undefined,
  }),
  component: ManualReviewPage,
});

const siteFollowRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'seguimiento',
  component: FollowTab,
});

const sitePagesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'paginas',
  validateSearch: (search: Record<string, unknown>): PagesSearch => ({
    scan:
      typeof search.scan === 'string' && /^[0-9A-Za-z-]{1,64}$/.test(search.scan) ? search.scan : undefined,
    p: pageKey(search.p),
    disp: search.disp === 'movil' ? 'movil' : undefined,
    e: typeof search.e === 'string' && /^[0-9a-f-]{36}\.\d{1,4}$/i.test(search.e) ? search.e : undefined,
    k:
      typeof search.k === 'string' && /^[\w:.-]{1,100}\|(violation|incomplete)$/.test(search.k)
        ? search.k
        : undefined,
  }),
  component: PagesTab,
});

const siteVerificationRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'verificacion',
  component: VerificationTab,
});

const siteStatementRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'declaracion',
  component: StatementTab,
});

const siteSealRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'sello',
  component: SealTab,
});

const siteWordPressRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'wordpress',
  component: WordPressTab,
});

const siteSettingsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'ajustes',
  component: SettingsTab,
});

/** The old way to read one page of a scan (`?vista=paginas&p=…`) now lives in the "Pages" tab. */
export interface ScanSearch {
  vista?: 'paginas';
  p?: string;
  disp?: 'movil';
}

const scanRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'scans/$scanId',
  validateSearch: (search: Record<string, unknown>): ScanSearch => ({
    vista: search.vista === 'paginas' ? 'paginas' : undefined,
    p: pageKey(search.p),
    disp: search.disp === 'movil' ? 'movil' : undefined,
  }),
  beforeLoad: ({ params, search }) => {
    if (search.vista === 'paginas') {
      throw redirect({
        to: '/sites/$siteId/paginas',
        params: { siteId: params.siteId },
        search: { scan: params.scanId, p: search.p, disp: search.disp },
        replace: true,
      });
    }
  },
  component: ScanPage,
});

const membersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organization',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: MembersPage,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organization/billing',
  // Where Stripe sends the person back to: after paying (with the session to confirm), without paying, or
  // from the portal.
  validateSearch: (
    search: Record<string, unknown>,
  ): { pago?: 'ok' | 'cancelado' | 'portal'; session?: string } => ({
    pago:
      search.pago === 'ok' || search.pago === 'cancelado' || search.pago === 'portal'
        ? search.pago
        : undefined,
    session:
      typeof search.session === 'string' && /^cs_[A-Za-z0-9_]{1,200}$/.test(search.session)
        ? search.session
        : undefined,
  }),
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: BillingPage,
});

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integrations',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: IntegrationsPage,
});

/** The address this page had when it was only the WordPress plugin: links and bookmarks still arrive. */
const wordpressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integrations/wordpress',
  beforeLoad: () => {
    throw redirect({ to: '/integrations', hash: 'integracion-wordpress' });
  },
});

/** Where the WordPress plugin sends the browser to approve the link (the parameters come from it). */
export interface ConnectSearch {
  site?: string;
  cb?: string;
  state?: string;
  challenge?: string;
  name?: string;
}

const text = (value: unknown, max: number) =>
  typeof value === 'string' && value.length > 0 && value.length <= max ? value : undefined;

const connectWordPressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect/wordpress',
  validateSearch: (search: Record<string, unknown>): ConnectSearch => ({
    site: text(search.site, 2048),
    cb: text(search.cb, 2048),
    state: text(search.state, 128),
    challenge: text(search.challenge, 64),
    name: text(search.name, 120),
  }),
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: ConnectWordPressPage,
});

/** Only for superadmins; anyone else lands on the home page (the API refuses them anyway). */
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  beforeLoad: async ({ context, location }) => {
    const me = await requireOrganization(context, location.href);
    if (!me.superadmin) throw redirect({ to: '/' });
  },
  component: UsersPage,
});

// The person's own account, apart from the settings of each website.
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  beforeLoad: () => {
    throw redirect({ to: '/account/profile' });
  },
});

const accountProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/profile',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: ProfilePage,
});

const accountSecurityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/security',
  beforeLoad: ({ context, location }) => requireOrganization(context, location.href),
  component: SecurityPage,
});

const newOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/new-organization',
  beforeLoad: ({ context, location }) => requireUser(context, location.href),
  component: NewOrganizationPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: withRedirectAndError,
  beforeLoad: visitorsOnly,
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: withRedirectAndError,
  beforeLoad: visitorsOnly,
  component: SignupPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  beforeLoad: visitorsOnly,
  component: ForgotPasswordPage,
});

// Better Auth sends people here from the email with ?token=… or ?error=INVALID_TOKEN.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: ResetPasswordPage,
});

// The link of the verification email lands here (Better Auth adds ?error=… when it cannot verify it); open to
// anyone, signed in or not, because what it says is only what happened to the account the link was for.
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  validateSearch: (search: Record<string, unknown>): { estado?: string; error?: string; next?: string } => ({
    estado: typeof search.estado === 'string' ? search.estado : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
    next: safeRedirect(search.next),
  }),
  component: VerifyEmailPage,
});

const resendVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resend-verification',
  validateSearch: withRedirect,
  beforeLoad: visitorsOnly,
  component: ResendVerificationPage,
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitation/$id',
  component: InvitationPage,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  newSiteRoute,
  siteRoute.addChildren([
    siteAnalysisRoute,
    sitePagesRoute,
    siteFollowRoute,
    siteManualRoute,
    siteVerificationRoute,
    siteStatementRoute,
    siteSealRoute,
    siteWordPressRoute,
    siteSettingsRoute,
    scanRoute,
  ]),
  membersRoute,
  billingRoute,
  integrationsRoute,
  wordpressRoute,
  connectWordPressRoute,
  usersRoute,
  accountRoute,
  accountProfileRoute,
  accountSecurityRoute,
  newOrganizationRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  resendVerificationRoute,
  invitationRoute,
]);

export function createAppRouter(queryClient: QueryClient) {
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPendingComponent: LoadingPage,
    defaultPreload: 'intent',
    // Preloaded data lives in TanStack Query, which decides when it is stale.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
  followNavigations(router);
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
